import { useMemo, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { LuGlobe } from "react-icons/lu";
import { SvgIcon } from "@mui/material";
import {
  AccountCircleOutlined,
  BusinessOutlined,
  HomeOutlined,
  InboxOutlined,
  LeaderboardOutlined,
  ListOutlined,
  LocalFireDepartmentOutlined,
  ManageAccountsOutlined,
  MonetizationOnOutlined,
  PersonAddAlt1Outlined,
  SendOutlined,
  GroupOutlined as GroupOutlinedIcon,
  AssistantOutlined,
  FilePresent,
} from "@mui/icons-material";
import { jobsApi } from "src/api/jobs";
import { paths } from "src/paths";
import { API_SERVICE_BACKEND } from "src/config";
import { getSessionStorage } from "src/utils/storage";
import { isFeatureEnabled } from "src/utils/featureFlags";
import Announcement01Icon from "@untitled-ui/icons-react/build/esm/Announcement01";

async function generateHMAC(message, key) {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(key);
  const messageData = encoder.encode(message);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyData,
    { name: "HMAC", hash: { name: "SHA-256" } },
    false,
    ["sign"]
  );

  const signature = await crypto.subtle.sign("HMAC", cryptoKey, messageData);
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export const useSections = () => {
  const { t } = useTranslation();

  const clientEmail = getSessionStorage("userEmail");
  const organizationId = getSessionStorage("organization_id");
  const fullName = getSessionStorage("fullName");

  useEffect(() => {
    if (!clientEmail) return;
    // Skip heavy Intercom stat fetching on emailreplies — not needed there
    if (window.location.pathname.includes("/dashboard/emailreplies")) return;

    let cancelled = false;
    const controller = new AbortController();

    async function init() {
      try {
        const campaignsRes = await jobsApi.getUserCampaigns(
          clientEmail,
          "",
          0,
          0,
          "all",
          "",
          "",
          "_id"
        );

        if (cancelled) return;

        const campaigns = campaignsRes?.data || [];
        const campaignIds = campaigns.map((c) => c._id).filter(Boolean);
        const campaignIdsStr = campaignIds.join(",");

        const meetPromise = (async () => {
          if (!campaignIds.length) return { last7: 0, last30: 0, unseen: 0 };

          try {
            const res = await axios.post(
              `${API_SERVICE_BACKEND}/dashboard-chart`,
              { campaignIds: campaignIdsStr },
              { signal: controller.signal }
            );

            const meetings = res?.data?.data?.meetings || [];

            const isWithinLastDays = (date, days) => {
              const today = new Date();
              const dateBefore = new Date(
                today.getFullYear(),
                today.getMonth(),
                today.getDate() - days
              );
              return date > dateBefore;
            };

            const calculateMeetings = (meetingsArr, days) =>
              meetingsArr
                .filter((m) => {
                  const meetingDate = new Date(m._id);
                  return isWithinLastDays(meetingDate, days);
                })
                .reduce((s, m) => s + (m.count || 0), 0);

            const last7 = calculateMeetings(meetings, 7);
            const last30 = calculateMeetings(meetings, 30);
            const unseen = meetings.reduce(
              (acc, item) => acc + (item.seen === false ? 1 : 0),
              0
            );

            return { last7, last30, unseen };
          } catch (err) {
            if (axios.isCancel(err)) return { last7: 0, last30: 0, unseen: 0 };
            throw err;
          }
        })();

        const [meetResult] = await Promise.all([meetPromise]);

        if (cancelled) return;

        let userHash = null;
        try {
          userHash = await generateHMAC(
            clientEmail,
            process.env.REACT_APP_INTERCOM_HASH_KEY
          );
        } catch (e) {
          //console.log("generateHMAC error", e);
        }

        if (window.Intercom) {
          window.Intercom("update", {
            email: clientEmail,
            name: fullName,
            user_hash: userHash,
            ai_agents_created: campaigns.length,
            campaigns_created: campaigns.length,
            meetings_booked_7days: meetResult.last7,
            meetings_booked_30days: meetResult.last30,
          });
        }
      } catch (err) {
        if (err.name === "CanceledError" || err.name === "AbortError") return;
        console.error("useSections init error:", err);
      }
    }

    init();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [clientEmail, organizationId, fullName]);

  return useMemo(() => {
    return [
      {
        subheader: "Leads Cloud",
        path: paths.dashboard.crm.index,
        icon: (
          <SvgIcon fontSize="medium">
            <GroupOutlinedIcon />
          </SvgIcon>
        ),
        items: [
          {
            title: t("People Search"),
            path: paths.dashboard.crm.peopleSearch,
            icon: (
              <SvgIcon fontSize="medium">
                <GroupOutlinedIcon />
              </SvgIcon>
            ),
          },
          {
            title: t("Company Search"),
            path: paths.dashboard.crm.companySearch,
            icon: (
              <SvgIcon fontSize="medium">
                <BusinessOutlined />
              </SvgIcon>
            ),
          },
          {
            title: t("Lists"),
            path: paths.dashboard.crm.lists,
            icon: (
              <SvgIcon fontSize="medium">
                <ListOutlined />
              </SvgIcon>
            ),
          },
          // {
          //   title: t("Leads"),
          //   path: paths.dashboard.crm.leads,
          //   icon: (
          //     <SvgIcon fontSize="medium">
          //       <GroupOutlinedIcon />
          //     </SvgIcon>
          //   ),
          // },
          {
            title: t("Data Enrichment"),
            path: paths.dashboard.crm.enrich,
            icon: (
              <SvgIcon fontSize="medium">
                <PersonAddAlt1Outlined />
              </SvgIcon>
            ),
          },
        ],
      },
      {
        subheader: "EngageOS",
        path: paths.dashboard.campaigns,
        icon: (
          <SvgIcon fontSize="medium">
            <Announcement01Icon />
          </SvgIcon>
        ),
        items: [
          {
            title: t("Dashboard"),
            path: paths.dashboard.dashboard,
            icon: (
              <SvgIcon fontSize="medium">
                <HomeOutlined />
              </SvgIcon>
            ),
          },
          {
            title: t("Campaigns"),
            path: paths.dashboard.campaigns,
            icon: (
              <SvgIcon fontSize="medium">
                <SendOutlined />
              </SvgIcon>
            ),
            childPaths: [
              paths.dashboard.contacts,
              `${paths.dashboard.campaigns}/create`,
              `${paths.dashboard.campaigns}/chat-campaign`,
            ],
          },
          {
            title: t("Analytics"),
            path: paths.dashboard.analytics,
            icon: (
              <SvgIcon fontSize="medium">
                <LeaderboardOutlined />
              </SvgIcon>
            ),
          },
          {
            title: t("Accounts"),
            path: paths.dashboard.emailAccounts.manage,
            icon: (
              <SvgIcon fontSize="medium">
                <AccountCircleOutlined />
              </SvgIcon>
            ),
          },
          {
            title: t("Unibox"),
            path: paths.dashboard.emailReplies,
            icon: (
              <SvgIcon fontSize="medium">
                <InboxOutlined />
              </SvgIcon>
            ),
          },
          {
            title: t("Deals"),
            path: paths.dashboard.crm.index,
            icon: (
              <SvgIcon fontSize="medium">
                <MonetizationOnOutlined />
              </SvgIcon>
            ),
          },
        ],
      },
      {
        subheader: "AI Agents",
        path: paths.dashboard.agents,
        icon: (
          <SvgIcon fontSize="medium">
            <AssistantOutlined />
          </SvgIcon>
        ),
        items: [
          ...(isFeatureEnabled("ONBOARDING")
            ? [
                {
                  title: t("Knowledge"),
                  path: paths.dashboard.knowledge,
                  icon: (
                    <SvgIcon fontSize="medium">
                      <FilePresent />
                    </SvgIcon>
                  ),
                  childPaths: [paths.dashboard.knowledge],
                }
              ]
            : []),
          {
            title: t("Agents"),
            path: paths.dashboard.agents,
            icon: (
              <SvgIcon fontSize="medium">
                <AssistantOutlined />
              </SvgIcon>
            ),
            childPaths: [paths.dashboard.agents],
          },
        ],
      },
      {
        subheader: "Partners",
        path: paths.dashboard.whiteLable.index,
        icon: (
          <SvgIcon fontSize="medium">
            <ManageAccountsOutlined />
          </SvgIcon>
        ),
        items: [
          {
            title: t("White Label"),
            path: paths.dashboard.whiteLable.index,
            icon: (
              <SvgIcon fontSize="medium">
                <ManageAccountsOutlined />
              </SvgIcon>
            ),
          },
          ...(isFeatureEnabled("MANAGED_DOMAINS")
            ? [
                {
                  title: "Domain",
                  path: paths.dashboard.domain,
                  icon: (
                    <SvgIcon fontSize="medium">
                      <LuGlobe />
                    </SvgIcon>
                  ),
                },
              ]
            : []),
        ],
      },
    ];
  }, [t]);
};
